// Digital signatures in a PDF (ISO 32000-1 §12.8): sign as an incremental update with a detached CMS signature (adbe.pkcs7.detached),
// and read back every signature in a file and check it. Signing adds a signature field, its widget and the signature dictionary, with
// room reserved for the signature in /Contents and the /ByteRange filled in once the final layout of the file is known.

import { PdfDocument } from "./document";
import { Dict, PName, PRef, type PObj } from "./parse";
import { Out, pdfString, serialize, writeXref } from "./writer";
import { concat, parse } from "./der";
import { signDetached, verifyDetached, type SignatureCheck, type SigningIdentity } from "./cms";

const SIG_BYTES = 8192;
const enc = new TextEncoder();
const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, ""));

export interface SignOptions {
    reason?: string;
    location?: string;
    /** 1-based page for the visible signature; with no `rect` the signature is invisible. */
    page?: number;
    /** The visible signature's box in user space (points). */
    rect?: [number, number, number, number] | null;
}

const pdfDate = (d: Date) => { const p = (n: number) => String(n).padStart(2, "0"); return `D:${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`; };

/** The signature fields of the document: [name, the signature dictionary] (only the ones that have been signed). */
function signedFields(doc: PdfDocument): Array<{ name: string; v: Dict }> {
    const form = doc.catalog.dict("AcroForm");
    const fields = form?.arr("Fields") ?? [];
    const out: Array<{ name: string; v: Dict }> = [];
    const seen = new Set<Dict>();
    const walk = (n: PObj, prefix: string, depth: number) => {
        const d = doc.xref.resolve(n);
        if (!(d instanceof Dict) || seen.has(d) || depth > 20) return;
        seen.add(d);
        const t = d.str("T");
        const name = t ? (prefix ? `${prefix}.${t}` : t) : prefix;
        const kids = d.arr("Kids");
        if (kids) for (const k of kids) walk(k, name, depth + 1);
        const v = d.dict("V");
        if (d.name("FT") === "Sig" && v && v.has("ByteRange") && v.has("Contents")) out.push({ name, v });
    };
    for (const f of fields) walk(f, "", 0);
    return out;
}

/** How many signature fields (signed or not) there are, to name the next one. */
function countSigFields(doc: PdfDocument): number {
    const fields = doc.catalog.dict("AcroForm")?.arr("Fields") ?? [];
    let n = 0;
    for (const f of fields) { const d = doc.xref.resolve(f); if (d instanceof Dict && d.name("FT") === "Sig") n++; }
    return n;
}

/** Signs the document: returns the file with the signature appended. */
export async function signPdf(original: Uint8Array, id: SigningIdentity, o: SignOptions = {}): Promise<Uint8Array> {
    const doc = PdfDocument.open(original);
    const xref = doc.xref;
    if (xref.trailer.has("Encrypt")) throw new Error("This PDF is encrypted, so it can't be signed here.");
    const root = xref.trailer.raw("Root");
    if (!(root instanceof PRef)) throw new Error("The PDF has no catalog reference.");
    const pages = doc.pages();
    const pageIdx = Math.max(0, Math.min(pages.length - 1, (o.page ?? 1) - 1));
    const page = pages[pageIdx]!;
    const pref = page.ref;
    if (!pref) throw new Error("The page could not be found in the file.");

    let next = Math.max(xref.trailer.num("Size"), ...[...xref.entries.keys()].map((k) => k + 1));
    const out = new Out();
    out.push(original);
    if (original[original.length - 1] !== 10) out.text("\n");
    const offsets: Array<[number, number]> = [];
    const obj = (n: number, body: string) => { offsets.push([n, out.length]); out.text(`${n} 0 obj\n${body}\nendobj\n`); };

    const sigNum = next++, widgetNum = next++;
    const visible = Boolean(o.rect);
    const apNum = visible ? next++ : 0;
    const now = new Date();

    // the signature dictionary, with the places to fill in later remembered
    offsets.push([sigNum, out.length]);
    out.text(`${sigNum} 0 obj\n<< /Type /Sig /Filter /Adobe.PPKLite /SubFilter /adbe.pkcs7.detached /ByteRange `);
    const byteRangeAt = out.length;
    out.text("[0 0000000000 0000000000 0000000000]");
    out.text(" /Contents ");
    const contentsStart = out.length;
    out.text("<" + "0".repeat(SIG_BYTES * 2) + ">");
    const contentsEnd = out.length;
    out.text(` /M (${pdfDate(now)}) /Name ${pdfString(id.name)}${o.reason ? ` /Reason ${pdfString(o.reason)}` : ""}${o.location ? ` /Location ${pdfString(o.location)}` : ""}${id.email ? ` /ContactInfo ${pdfString(id.email)}` : ""} >>\nendobj\n`);

    // the visible appearance
    const rect = o.rect ?? [0, 0, 0, 0];
    if (visible) {
        const w = rect[2] - rect[0], h = rect[3] - rect[1];
        const size = Math.max(6, Math.min(11, h / 4));
        const lines = [`Digitally signed by ${id.name}`, `Date: ${now.toISOString().slice(0, 16).replace("T", " ")} UTC`, ...(o.reason ? [`Reason: ${o.reason}`] : [])];
        let c = `0.95 0.97 1 rg 0 0 ${num(w)} ${num(h)} re f 0.2 0.35 0.7 RG 0.8 w 0.4 0.4 ${num(w - 0.8)} ${num(h - 0.8)} re S\nBT /F1 ${num(size)} Tf 0.1 0.1 0.25 rg\n`;
        lines.forEach((ln, i) => { c += `1 0 0 1 ${num(4)} ${num(h - size * 1.35 * (i + 1))} Tm ${pdfString(ln.replace(/[^\x20-\x7e]/g, "?"))} Tj\n`; });
        c += "ET\n";
        const data = enc.encode(c);
        offsets.push([apNum, out.length]);
        out.text(`${apNum} 0 obj\n<< /Type /XObject /Subtype /Form /BBox [0 0 ${num(w)} ${num(h)}] /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> >> >> /Length ${data.length} >>\nstream\n`);
        out.push(data);
        out.text("\nendstream\nendobj\n");
    }

    // the field and its widget (one object), on the page
    const fieldName = `Signature${countSigFields(doc) + 1}`;
    obj(widgetNum, `<< /Type /Annot /Subtype /Widget /FT /Sig /T ${pdfString(fieldName)} /V ${sigNum} 0 R /Rect [${rect.map(num).join(" ")}] /P ${pref.num} ${pref.gen} R /F 132${visible ? ` /AP << /N ${apNum} 0 R >>` : ""} >>`);

    // the page lists the widget
    const annotsRaw = page.dict.raw("Annots");
    if (annotsRaw instanceof PRef) {
        const arr = xref.fetch(annotsRaw);
        const existing = Array.isArray(arr) ? arr.map((x) => serialize(x)).join(" ") : "";
        obj(annotsRaw.num, `[${existing} ${widgetNum} 0 R]`);
    } else {
        const d = new Dict(null);
        for (const k of page.dict.keys()) if (k !== "Annots") d.set(k, page.dict.raw(k)!);
        const existing = Array.isArray(annotsRaw) ? annotsRaw.map((x) => serialize(x)).join(" ") : "";
        obj(pref.num, serialize(d).slice(0, -2) + `/Annots [${existing} ${widgetNum} 0 R] >>`);
    }

    // the form: the field joins /Fields and /SigFlags says the file has signatures and is append-only
    const afRaw = doc.catalog.raw("AcroForm");
    const af = doc.catalog.dict("AcroForm");
    const keep = new Dict(xref);
    if (af) for (const k of af.keys()) if (k !== "Fields" && k !== "SigFlags") keep.set(k, af.raw(k)!);
    const existing = (af?.arr("Fields") ?? []).map((x) => serialize(x)).join(" ");
    const body = serialize(keep).slice(0, -2) + `/Fields [${existing} ${widgetNum} 0 R] /SigFlags 3 >>`;
    if (afRaw instanceof PRef) obj(afRaw.num, body);
    else {
        const cat = new Dict(xref);
        for (const k of doc.catalog.keys()) if (k !== "AcroForm") cat.set(k, doc.catalog.raw(k)!);
        obj(root.num, serialize(cat).slice(0, -2) + `/AcroForm ${body} >>`);
    }

    writeXref(out, offsets, next, original, xref, root);
    const bytes = out.bytes();

    // now the file's layout is final: the ranges the signature covers, then the signature itself
    const total = bytes.length;
    const pad = (n: number) => String(n).padStart(10, "0");
    bytes.set(enc.encode(`[0 ${pad(contentsStart)} ${pad(contentsEnd)} ${pad(total - contentsEnd)}]`), byteRangeAt);
    const signed = concat(bytes.subarray(0, contentsStart), bytes.subarray(contentsEnd));
    const cms = await signDetached(signed, id, now);
    if (cms.length > SIG_BYTES) throw new Error("The signature is larger than the space reserved for it.");
    let hex = "";
    for (const b of cms) hex += b.toString(16).padStart(2, "0");
    bytes.set(enc.encode(hex.toUpperCase()), contentsStart + 1);
    return bytes;
}

export interface SignatureInfo extends SignatureCheck {
    field: string;
    reason: string;
    location: string;
    /** The signed ranges reach the end of the file: nothing was added after signing. */
    coversWholeFile: boolean;
    byteRange: number[];
}

/** Every signature in the file, checked. */
export async function readSignatures(bytes: Uint8Array): Promise<SignatureInfo[]> {
    const doc = PdfDocument.open(bytes);
    const out: SignatureInfo[] = [];
    for (const { name, v } of signedFields(doc)) {
        const br = (v.arr("ByteRange") ?? []).map((x) => (typeof doc.xref.resolve(x) === "number" ? (doc.xref.resolve(x) as number) : 0));
        const raw = v.str("Contents") ?? "";
        const cms = Uint8Array.from(raw, (c) => c.charCodeAt(0) & 255);
        const text = (k: string) => { const s = v.str(k); return s ? s.replace(/^þÿ/, "") : ""; };
        let check: SignatureCheck;
        if (br.length !== 4 || br[1]! + br[3]! <= 0 || br[2]! + br[3]! > bytes.length) {
            check = { signer: "", email: "", signedAt: null, intact: false, signatureValid: false, selfSigned: false, notBefore: null, notAfter: null, problem: "The signature's byte ranges do not fit the file." };
        } else {
            const signed = concat(bytes.subarray(br[0]!, br[0]! + br[1]!), bytes.subarray(br[2]!, br[2]! + br[3]!));
            let der: Uint8Array = cms;
            try { const n = parse(cms)[0]; if (n) der = n.raw; } catch { /* verify reports it */ }
            check = await verifyDetached(der, signed);
        }
        out.push({ ...check, field: name, reason: text("Reason"), location: text("Location"), byteRange: br, coversWholeFile: br.length === 4 && br[2]! + br[3]! === bytes.length });
    }
    return out;
}

void PName;
